import exported from '@/data/equipment-display.json';
import { getItemName } from '@/data/item-names';
interface StatDefinition { name: string; title: string; percentage: boolean }
interface ItemDefinition { name: string; baseRarity: number; baseStats?: { stats: number; value: number }; rune?: { description: string; values: number[][] } }
const catalog = exported as { stats: Record<string, StatDefinition>; rarities: Record<string, { title: string; color: string }>; items: Record<string, ItemDefinition> };
export interface EquipmentStat { title: string; value: string; enumId: number; kind: 'main' | 'sub' | 'additional' }
export interface EquipmentRune { id: string | null; title: string; description: string; color: string }
export interface EquipmentDisplay { id: string; title: string; color: string; rarity: string; base: EquipmentStat[]; additional: EquipmentStat[]; runes: EquipmentRune[] }
const object = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const number = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;
const integer = (value: unknown): number | null => Number.isInteger(value) && typeof value === 'number' ? value : null;
const f = Math.fround;
/** Mirror float32 GetUpgradeValue, then GetEnhancedValue (ceil before floor). */
export function enhancedEquipmentValue(value: number, baseRarity: number, rarity: number, level: number): number {
  const upgraded = Math.ceil(f(f(value) + f(f(.25 * (rarity - baseRarity)) * f(value))));
  let modifier: number;
  if (level <= 5) modifier = f(1 + f(level * f(.1)));
  else if (level <= 10) modifier = f(1 + f(.5 + f((level - 5) * f(.15))));
  else modifier = f(1 + f(1.25 + f((level - 10) * f(.2))));
  return Math.floor(f(upgraded * modifier));
}
export function formatEquipmentStat(enumId: number, value: number | null): string {
  if (value === null) return '—';
  const definition = catalog.stats[enumId];
  if (!definition) return `${value} (raw)`;
  return definition.percentage ? `${(value / 10).toFixed(2)}%` : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
export function equipmentDisplay(saved: unknown): EquipmentDisplay {
  const item = object(saved), id = typeof item.id === 'string' ? item.id : '';
  const definition = catalog.items[id];
  const rarity = integer(item.currentRarity), level = integer(item.enhancementLevel);
  const appearance = rarity === null ? undefined : catalog.rarities[rarity];
  const base: EquipmentStat[] = [], additional: EquipmentStat[] = [];
  function stat(raw: unknown, kind: EquipmentStat['kind'], scale: boolean): EquipmentStat | null {
    const s = object(raw), enumId = integer(s.stats);
    if (enumId === null || enumId === 0) return null;
    let value = number(s.value);
    if (scale) value = value !== null && definition && rarity !== null && level !== null ? enhancedEquipmentValue(value, definition.baseRarity, rarity, level) : null;
    return { title: catalog.stats[enumId]?.title ?? `Unknown stat (${enumId})`, value: formatEquipmentStat(enumId, value), enumId, kind };
  }
  const main = stat(definition?.baseStats, 'main', true), sub = stat(item.randomStat, 'sub', true);
  if (main) base.push(main);
  if (sub) base.push(sub);
  const fixed = Array.isArray(item.fixedStats) ? item.fixedStats : [];
  // Inspector supports the extra third/fourth saved affixes starting at Rare.
  const count = rarity === null ? 4 : rarity >= 2 ? 4 : rarity >= 1 ? 1 : 0;
  for (const raw of fixed.slice(0, count)) { const s = stat(raw, 'additional', false); if (s) additional.push(s); }
  const sockets = Array.isArray(item.socketStats) ? item.socketStats : [];
  const socketCount = rarity === null ? Math.min(2, sockets.length) : rarity >= 4 ? 2 : rarity >= 2 ? 1 : 0;
  const runes = Array.from({ length: socketCount }, (_, index): EquipmentRune => {
    const socket = object(sockets[index]), runeId = typeof socket.socketID === 'string' ? socket.socketID : '';
    if (!runeId) return { id: null, title: 'Empty socket', description: '—', color: '#8e8997' };
    const rune = catalog.items[runeId], runeLevel = integer(socket.currentLevel);
    const description = !rune?.rune ? 'Rune definition unavailable' : runeLevel === null || runeLevel < 0 ? 'Rune level not recorded' : rune.rune.description.replace(/\{(\d+)\}/g, (_, i: string) => {
      const table = rune.rune!.values[Number(i)];
      return table?.length ? String(table[Math.min(runeLevel, table.length - 1)]) : '—';
    });
    return { id: runeId, title: `${rune?.name ?? getItemName(runeId)}${runeLevel && runeLevel > 0 ? ` +${runeLevel}` : ''}`, description, color: catalog.rarities[rune?.baseRarity]?.color ?? '#ededf0' };
  });
  return { id, title: `${definition?.name ?? getItemName(id)}${level && level > 0 ? ` +${level}` : ''}`, color: appearance?.color ?? '#ededf0', rarity: appearance?.title ?? 'Unknown rarity', base, additional, runes };
}
